import { useEffect, useRef, useCallback } from "react"
import type { ChatMessage } from "@/contexts/AuthContext"
import type {
  ChatWSEvent,
  BroadcastMessage,
  BroadcastReaction,
  BroadcastPoll,
} from "@/lib/chatBroadcast"

// ---- Transform broadcast → per-user ChatMessage ----

function broadcastReactionsToChatReactions(
  reactions: BroadcastReaction[],
  userId: string
): ChatMessage["reactions"] {
  return reactions.map((r) => ({
    emoji: r.emoji,
    count: r.userIds.length,
    reactedByCurrentUser: r.userIds.includes(userId),
  }))
}

function broadcastPollToChatPoll(
  poll: BroadcastPoll | undefined,
  userId: string
): ChatMessage["poll"] {
  if (!poll) return undefined
  const options = poll.options.map((o) => ({
    id: o.id,
    text: o.text,
    emoji: o.emoji,
    votes: o.voterIds.length,
    selectedByCurrentUser: o.voterIds.includes(userId),
  }))
  return {
    question: poll.question,
    options,
    allowMultiSelect: poll.allowMultiSelect,
    closesAt: poll.closesAt,
    totalVotes: options.reduce((sum, o) => sum + o.votes, 0),
  }
}

function broadcastToChatMessage(
  msg: BroadcastMessage,
  userId: string,
  mutedUserIds: string[]
): ChatMessage {
  return {
    _id: msg._id,
    senderId: msg.senderId,
    senderName: msg.senderName,
    senderProfilePicture: msg.senderProfilePicture || null,
    senderIsAgencyAdmin: Boolean(msg.senderIsAgencyAdmin),
    content: msg.content,
    isSystem: Boolean(msg.isSystem),
    messageType: msg.messageType,
    fileUrl: msg.fileUrl,
    fileName: msg.fileName,
    fileType: msg.fileType,
    gifUrl: msg.gifUrl,
    sticker: msg.sticker,
    ctas: msg.ctas,
    reactions: broadcastReactionsToChatReactions(msg.reactions, userId),
    poll: broadcastPollToChatPoll(msg.poll, userId),
    isPinned: msg.isPinned,
    isDeleted: msg.isDeleted,
    editedAt: msg.editedAt ?? null,
    isEdited: Boolean(msg.editedAt),
    senderMuted: mutedUserIds.includes(msg.senderId),
    createdAt: msg.createdAt,
    isOwn: msg.senderId === userId,
  }
}

// ---- Hook ----

interface UseChatWebSocketOptions {
  token: string | null
  agencyId: string | null
  userId: string | null
  mutedUserIds: string[]
  onNewMessage: (msg: ChatMessage) => void
  onUpdateMessage: (msg: ChatMessage) => void
  onDeleteMessage: (messageId: string) => void
  onPinChanged: (msg: ChatMessage, unpinnedMessageId?: string) => void
  onMuteChanged: (mutedUserIds: string[]) => void
  onReconnect: () => void
}

export function useChatWebSocket({
  token,
  agencyId,
  userId,
  mutedUserIds,
  onNewMessage,
  onUpdateMessage,
  onDeleteMessage,
  onPinChanged,
  onMuteChanged,
  onReconnect,
}: UseChatWebSocketOptions) {
  const wsRef = useRef<WebSocket | null>(null)
  const retriesRef = useRef(0)
  const pingIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const unmountedRef = useRef(false)

  // Keep latest callbacks in refs to avoid reconnecting when they change
  const mutedRef = useRef(mutedUserIds)
  mutedRef.current = mutedUserIds

  const cbRef = useRef({
    onNewMessage,
    onUpdateMessage,
    onDeleteMessage,
    onPinChanged,
    onMuteChanged,
    onReconnect,
  })
  cbRef.current = {
    onNewMessage,
    onUpdateMessage,
    onDeleteMessage,
    onPinChanged,
    onMuteChanged,
    onReconnect,
  }

  const connect = useCallback(() => {
    if (!token || !agencyId || !userId) return
    if (unmountedRef.current) return

    const proto = window.location.protocol === "https:" ? "wss:" : "ws:"
    const url = `${proto}//${window.location.host}/ws/chat?token=${encodeURIComponent(token)}&agencyId=${encodeURIComponent(agencyId)}`

    const ws = new WebSocket(url)
    wsRef.current = ws

    ws.onopen = () => {
      retriesRef.current = 0

      // Start ping keepalive
      pingIntervalRef.current = setInterval(() => {
        if (ws.readyState === WebSocket.OPEN) {
          ws.send("ping")
        }
      }, 30_000)
    }

    ws.onmessage = (ev) => {
      if (ev.data === "pong") return

      let event: ChatWSEvent
      try {
        event = JSON.parse(ev.data)
      } catch {
        return
      }

      const cbs = cbRef.current
      const muted = mutedRef.current
      const uid = userId

      switch (event.type) {
        case "new_message":
          cbs.onNewMessage(broadcastToChatMessage(event.message, uid, muted))
          break
        case "update_message":
          cbs.onUpdateMessage(broadcastToChatMessage(event.message, uid, muted))
          break
        case "delete_message":
          cbs.onDeleteMessage(event.messageId)
          break
        case "pin_changed":
          cbs.onPinChanged(
            broadcastToChatMessage(event.message, uid, muted),
            event.unpinnedMessageId
          )
          break
        case "mute_changed":
          cbs.onMuteChanged(event.mutedUserIds)
          break
      }
    }

    ws.onclose = () => {
      cleanup()
      if (unmountedRef.current) return

      // Exponential backoff: 1s, 2s, 4s, 8s, ... max 30s
      const delay = Math.min(1000 * 2 ** retriesRef.current, 30_000)
      retriesRef.current++

      reconnectTimeoutRef.current = setTimeout(() => {
        cbRef.current.onReconnect()
        connect()
      }, delay)
    }

    ws.onerror = () => {
      // onclose will fire after onerror
    }
  }, [token, agencyId, userId])

  function cleanup() {
    if (pingIntervalRef.current) {
      clearInterval(pingIntervalRef.current)
      pingIntervalRef.current = null
    }
  }

  useEffect(() => {
    unmountedRef.current = false
    connect()

    return () => {
      unmountedRef.current = true
      cleanup()
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current)
        reconnectTimeoutRef.current = null
      }
      if (wsRef.current) {
        wsRef.current.onclose = null // prevent reconnect on intentional close
        wsRef.current.close()
        wsRef.current = null
      }
    }
  }, [connect])
}
